import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { BASE_URL_V2 } from 'shared/constants';

// Base interface for category with common fields
export interface CategoryBase {
  id: number;
  name: string;
}

// Root category interface
export interface CategoryRoot extends CategoryBase {
  client_id: number;
  order_by: number;
  last_modified: string;
}

// Prime category interface
export interface CategoryPrime extends CategoryBase {
  category_root_id: number;
  is_active: boolean;
  category_root: CategoryRoot;
  category_second: CategorySecond[];
}

// Second category interface
export interface CategorySecond extends CategoryBase {
  url: string;
  category_prime_id: number;
  is_active: boolean;
  category_prime: CategoryPrime;
}

// Categories interface (top-level)
export interface Categories extends CategoryRoot {
  category_prime: CategoryPrime[];
}

export interface CreateCategoryPrime {
  name: string;
  category_second: string[];
}

export interface CreateCategoryRoot {
  category_root: string;
  category_prime: CreateCategoryPrime[];
}

export interface CreateMultiplePrimes {
  rootId: number;
  primeNames: string[];
}

export interface CreateMultipleSeconds {
  categoryPrimeId: number;
  secondNames: string[];
}

export interface PrimeAndSeconds {
  primeName: string;
  secondNames: string[];
}

/**
 * Custom hook that provides API functions for managing categories in a hierarchical structure.
 * Categories are organized in three levels: Root → Prime → Second (Product Categories).
 * @returns {object} An object containing all category management API functions
 * @example
 * ```typescript
 * const { getAllCategories, createRootsWithPrimeAndSeconds } = useCategoriesApi();
 *
 * // Fetch all categories
 * const categories = await getAllCategories(profileId);
 *
 * // Create new category structure
 * const newCategories = await createRootsWithPrimeAndSeconds(categoryData, profileId);
 * ```
 */
export const useCategoriesApi = () => {
  const { axios } = useAxios(BASE_URL_V2);

  /**
   * Retrieves all categories from the API with their complete hierarchical structure.
   * @param {number} [profileId] - Optional profile ID to filter categories by profile
   * @returns {Promise<Categories[] | undefined>} Promise that resolves to an array of categories with their prime and second categories, or undefined if an error occurs
   * @example
   * ```typescript
   * const { getAllCategories } = useCategoriesApi();
   *
   * // Get all categories
   * const categories = await getAllCategories();
   *
   * // Get categories for specific profile
   * const profileCategories = await getAllCategories(123);
   *
   * if (categories) {
   *   categories.forEach(root => {
   *     console.log(`Root: ${root.name}`);
   *     root.category_prime.forEach(prime => {
   *       console.log(`  Prime: ${prime.name}`);
   *       prime.category_second.forEach(second => {
   *         console.log(`    Second: ${second.name}`);
   *       });
   *     });
   *   });
   * }
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const getAllCategories = async (profileId?: number): Promise<Categories[] | undefined> => {
    try {
      const response = await axios.get(`/categories`, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to fetch categories'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Creates multiple root categories with their associated prime and second categories in a single operation.
   * This is useful for bulk creation of complete category hierarchies.
   * @param {CreateCategoryRoot[]} data - Array of category root objects with nested prime and second categories
   * @param {number} [profileId] - Optional profile ID to associate the categories with
   * @returns {Promise<Categories[] | undefined>} Promise that resolves to an array of created categories, or undefined if an error occurs
   * @example
   * ```typescript
   * const { createRootsWithPrimeAndSeconds } = useCategoriesApi();
   *
   * const categoryData = [
   *   {
   *     category_root: "Electronics",
   *     category_prime: [
   *       {
   *         name: "Computers",
   *         category_second: ["Laptops", "Desktops", "Tablets"]
   *       },
   *       {
   *         name: "Mobile",
   *         category_second: ["Smartphones", "Accessories"]
   *       }
   *     ]
   *   }
   * ];
   *
   * const newCategories = await createRootsWithPrimeAndSeconds(categoryData, profileId);
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const createRootsWithPrimeAndSeconds = async (
    data: CreateCategoryRoot[],
    profileId?: number
  ): Promise<Categories[] | undefined> => {
    try {
      const response = await axios.post(`/categories`, data, {
        params: { profileId }
      });
      if (response?.status === 201) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to create categories'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Updates an existing root category with new data.
   * @param {number} id - The ID of the root category to update
   * @param {Partial<CategoryRoot>} data - Partial object containing the fields to update
   * @param {number} [profileId] - Optional profile ID for the operation
   * @returns {Promise<CategoryRoot | undefined>} Promise that resolves to the updated root category, or undefined if an error occurs
   * @example
   * ```typescript
   * const { updateRoot } = useCategoriesApi();
   *
   * // Update root category name
   * const updatedRoot = await updateRoot(123, { name: "New Category Name" });
   *
   * // Update multiple fields
   * const updatedRoot = await updateRoot(123, {
   *   name: "Updated Name",
   *   order_by: 5
   * });
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const updateRoot = async (
    id: number,
    data: Partial<CategoryRoot>,
    profileId?: number
  ): Promise<CategoryRoot | undefined> => {
    try {
      const response = await axios.patch(`/categories/root/${id}`, data, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to update category'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Deletes a root category and all its associated prime and second categories.
   * This is a cascading delete operation.
   * @param {number} id - The ID of the root category to delete
   * @param {number} [profileId] - Optional profile ID for the operation
   * @returns {Promise<CategoryRoot | undefined>} Promise that resolves to the deleted root category, or undefined if an error occurs
   * @example
   * ```typescript
   * const { deleteCategory } = useCategoriesApi();
   *
   * // Delete a root category
   * const deletedCategory = await deleteCategory(123);
   *
   * if (deletedCategory) {
   *   console.log(`Deleted category: ${deletedCategory.name}`);
   * }
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const deleteCategory = async (id: number, profileId?: number): Promise<CategoryRoot | undefined> => {
    try {
      const response = await axios.delete(`/categories/root/${id}`, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to delete category'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Creates multiple prime categories under a specific root category.
   * @param {CreateMultiplePrimes} data - Object containing root ID and array of prime category names
   * @param {number} [profileId] - Optional profile ID for the operation
   * @returns {Promise<CategoryPrime[] | undefined>} Promise that resolves to an array of created prime categories, or undefined if an error occurs
   * @example
   * ```typescript
   * const { createPrimes } = useCategoriesApi();
   *
   * // Create multiple prime categories under root ID 123
   * const newPrimes = await createPrimes({
   *   rootId: 123,
   *   primeNames: ["Electronics", "Clothing", "Books"]
   * });
   *
   * if (newPrimes) {
   *   newPrimes.forEach(prime => {
   *     console.log(`Created prime category: ${prime.name}`);
   *   });
   * }
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const createPrimes = async (data: CreateMultiplePrimes, profileId?: number): Promise<CategoryPrime[] | undefined> => {
    try {
      const response = await axios.post(`/categories/prime`, data, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to create sub categories'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Updates an existing prime category with new data.
   * @param {number} id - The ID of the prime category to update
   * @param {Partial<CategoryPrime>} data - Partial object containing the fields to update
   * @param {number} [profileId] - Optional profile ID for the operation
   * @returns {Promise<CategoryPrime | undefined>} Promise that resolves to the updated prime category, or undefined if an error occurs
   * @example
   * ```typescript
   * const { updatePrime } = useCategoriesApi();
   *
   * // Update prime category name
   * const updatedPrime = await updatePrime(456, { name: "Updated Prime Name" });
   *
   * // Update active status
   * const updatedPrime = await updatePrime(456, { is_active: false });
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const updatePrime = async (
    id: number,
    data: Partial<CategoryPrime>,
    profileId?: number
  ): Promise<CategoryPrime | undefined> => {
    try {
      const response = await axios.patch(`/categories/prime/${id}`, data, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to update sub category'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Deletes a prime category and all its associated second categories.
   * This is a cascading delete operation.
   * @param {number} id - The ID of the prime category to delete
   * @param {number} [profileId] - Optional profile ID for the operation
   * @returns {Promise<CategoryPrime | undefined>} Promise that resolves to the deleted prime category, or undefined if an error occurs
   * @example
   * ```typescript
   * const { deletePrime } = useCategoriesApi();
   *
   * // Delete a prime category
   * const deletedPrime = await deletePrime(456);
   *
   * if (deletedPrime) {
   *   console.log(`Deleted prime category: ${deletedPrime.name}`);
   * }
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const deletePrime = async (id: number, profileId?: number): Promise<CategoryPrime | undefined> => {
    try {
      const response = await axios.delete(`/categories/prime/${id}`, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to delete sub category'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Creates multiple second (product) categories under a specific prime category.
   * @param {CreateMultipleSeconds} data - Object containing prime category ID and array of second category names
   * @param {number} [profileId] - Optional profile ID for the operation
   * @returns {Promise<CategorySecond[] | undefined>} Promise that resolves to an array of created second categories, or undefined if an error occurs
   * @example
   * ```typescript
   * const { createSeconds } = useCategoriesApi();
   *
   * // Create multiple second categories under prime ID 456
   * const newSeconds = await createSeconds({
   *   categoryPrimeId: 456,
   *   secondNames: ["Laptops", "Desktops", "Tablets", "Accessories"]
   * });
   *
   * if (newSeconds) {
   *   newSeconds.forEach(second => {
   *     console.log(`Created second category: ${second.name}`);
   *   });
   * }
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const createSeconds = async (
    data: CreateMultipleSeconds,
    profileId?: number
  ): Promise<CategorySecond[] | undefined> => {
    try {
      const response = await axios.post(`/categories/seconds`, data, {
        params: { profileId }
      });
      if (response?.status === 201) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to create product categories'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Updates an existing second (product) category with new data.
   * @param {number} id - The ID of the second category to update
   * @param {Partial<CategorySecond>} data - Partial object containing the fields to update
   * @param {number} [profileId] - Optional profile ID for the operation
   * @returns {Promise<CategorySecond | undefined>} Promise that resolves to the updated second category, or undefined if an error occurs
   * @example
   * ```typescript
   * const { updateSecond } = useCategoriesApi();
   *
   * // Update second category name
   * const updatedSecond = await updateSecond(789, { name: "Updated Product Name" });
   *
   * // Update URL
   * const updatedSecond = await updateSecond(789, { url: "/new-url" });
   *
   * // Update active status
   * const updatedSecond = await updateSecond(789, { is_active: false });
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const updateSecond = async (
    id: number,
    data: Partial<CategorySecond>,
    profileId?: number
  ): Promise<CategorySecond | undefined> => {
    try {
      const response = await axios.patch(`/categories/second/${id}`, data, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to update product category'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Deletes a second (product) category.
   * @param {number} id - The ID of the second category to delete
   * @param {number} [profileId] - Optional profile ID for the operation
   * @returns {Promise<CategorySecond | undefined>} Promise that resolves to the deleted second category, or undefined if an error occurs
   * @example
   * ```typescript
   * const { deleteSecond } = useCategoriesApi();
   *
   * // Delete a second category
   * const deletedSecond = await deleteSecond(789);
   *
   * if (deletedSecond) {
   *   console.log(`Deleted second category: ${deletedSecond.name}`);
   * }
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const deleteSecond = async (id: number, profileId?: number): Promise<CategorySecond | undefined> => {
    try {
      const response = await axios.delete(`/categories/second/${id}`, {
        params: { profileId }
      });
      if (response?.status === 200) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to delete product category'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Creates multiple prime categories and their associated second categories under a specific root category.
   * This is useful for bulk creation of prime and second categories in a single operation.
   * @param {number} id - The ID of the root category to add primes and seconds to
   * @param {PrimeAndSeconds[]} data - Array of objects containing prime name and associated second category names
   * @param {number} [profileId] - Optional profile ID for the operation
   * @returns {Promise<Categories[] | undefined>} Promise that resolves to an array of updated categories, or undefined if an error occurs
   * @example
   * ```typescript
   * const { createPrimesAndSeconds } = useCategoriesApi();
   *
   * // Create primes and seconds under root ID 123
   * const updatedCategories = await createPrimesAndSeconds(123, [
   *   {
   *     primeName: "Electronics",
   *     secondNames: ["Laptops", "Desktops", "Tablets"]
   *   },
   *   {
   *     primeName: "Clothing",
   *     secondNames: ["Men", "Women", "Kids"]
   *   }
   * ]);
   *
   * if (updatedCategories) {
   *   console.log(`Updated ${updatedCategories.length} categories`);
   * }
   * ```
   * @throws {Error} If the API call fails, an error is thrown with a relevant message
   */
  const createPrimesAndSeconds = async (
    id: number,
    data: PrimeAndSeconds[],
    profileId?: number
  ): Promise<Categories[] | undefined> => {
    try {
      const response = await axios.post(`/categories/root/${id}/primes-and-seconds`, data, {
        params: { profileId }
      });
      if (response?.status === 201) {
        return response?.data;
      }
      return Promise.reject(Error(response?.data?.message || 'Failed to create primes and seconds'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  return {
    getAllCategories,
    createRootsWithPrimeAndSeconds,
    updateRoot,
    deleteCategory,
    createPrimes,
    updatePrime,
    deletePrime,
    createSeconds,
    updateSecond,
    deleteSecond,
    createPrimesAndSeconds
  };
};
